/**
 * physicianOrderSheetWordService.js
 *
 * Web version ของ generatePhysicianOrderSheet
 * ย้ายมาจาก Expo (expo-file-system / expo-sharing) → ใช้ Blob + <a> download แทน
 *
 * การใช้งานใน EditVN.jsx:
 *   import { generatePhysicianOrderSheet } from '../services/physicianOrderSheetWordService';
 *   ...
 *   generatePhysicianOrderSheet(patient);
 */

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * สร้างและดาวน์โหลดไฟล์ Word (.doc) Physician's Order Sheet
 *
 * @param {object} patient — ข้อมูลผู้ป่วย (patient_name, hn, age, room_number, ward_name,
 *                           registration_date, medications[])
 */
export const generatePhysicianOrderSheet = (patient) => {
  try {
    const htmlContent = generateHTMLContent(patient);

    const blob = new Blob(['\ufeff', htmlContent], {
      type: 'application/msword;charset=utf-8',
    });

    const safeName = (patient.patient_name || 'patient').replace(/\s+/g, '_');
    const filename  = `Physicians_Order_Sheet_${safeName}_${Date.now()}.doc`;

    const url = URL.createObjectURL(blob);
    const a   = document.createElement('a');
    a.href     = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log('✅ Physician Order Sheet downloaded:', filename);
  } catch (error) {
    console.error('❌ Error generating Physician Order Sheet:', error);
    throw new Error('ไม่สามารถสร้าง Word ได้: ' + error.message);
  }
};

// ---------------------------------------------------------------------------
// HTML generation
// ---------------------------------------------------------------------------

function generateHTMLContent(patient) {
  const currentDate = new Date().toLocaleDateString('th-TH', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  const registrationDate = patient.registration_date
    ? new Date(patient.registration_date).toLocaleDateString('th-TH', {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : '-';

  return `
<html xmlns:o='urn:schemas-microsoft-com:office:office'
      xmlns:w='urn:schemas-microsoft-com:office:word'
      xmlns='http://www.w3.org/TR/REC-html40'>
<head>
  <meta charset="utf-8">
  <title>Physician's Order Sheet</title>
  <!--[if gte mso 9]><xml>
    <w:WordDocument>
      <w:View>Print</w:View>
      <w:Zoom>100</w:Zoom>
      <w:DoNotOptimizeForBrowser/>
    </w:WordDocument>
  </xml><![endif]-->
  <style>
    @page { size: A4 portrait; margin: 0.5in; }
    @page Section1 { size: 8.3in 11.7in; margin: 0.5in; }
    div.Section1 { page: Section1; width: 100%; }

    * { box-sizing: border-box; }
    body {
      font-family: 'TH SarabunPSK', 'Cordia New', 'Angsana New', sans-serif;
      font-size: 16pt; margin: 0; padding: 0; width: 100%;
    }

    .header { text-align: center; margin-bottom: 3px; border-bottom: 2px solid #2C3E50; padding-bottom: 2px; }
    .header h1 { font-size: 16pt; font-weight: bold; margin: 1px 0; color: #2C3E50; }
    .header h2 { font-size: 11pt; margin: 1px 0; color: #555; }

    .patient-info {
      margin-bottom: 3px; background-color: #F8F9FA;
      padding: 3px 6px; border-radius: 3px; border-left: 3px solid #2C3E50;
    }
    .patient-info-row { margin-bottom: 1px; font-size: 9pt; line-height: 1.2; }
    .patient-info-label { font-weight: bold; color: #555; display: inline-block; min-width: 70px; }
    .patient-info-value { color: #000; }

    .instructions {
      background-color: #FFF9E6; border-left: 3px solid #FFC107;
      padding: 3px 6px; margin-bottom: 3px; font-size: 8pt; color: #856404; line-height: 1.2;
    }
    .instructions strong { color: #FF6B6B; }

    table {
      width: 100% !important; border-collapse: collapse;
      margin-bottom: 0; table-layout: fixed !important; border: 1px solid #333333;
    }
    th, td {
      border: 1px solid #333333; padding: 6px; text-align: left;
      vertical-align: top; font-size: 11pt; word-wrap: break-word;
    }
    th {
      background-color: white; color: black; font-weight: bold;
      font-size: 9pt; padding: 5px 3px; text-align: center; line-height: 1.2;
    }

    .col-datetime    { width: 12%; }
    .col-one-day     { width: 38%; }
    .col-continuation{ width: 38%; }

    .order-cell { padding: 6px; vertical-align: top; height: 7.9in; min-height: 7.9in; }
    tbody tr, tbody td { height: 7.9in; min-height: 7.9in; }

    .medication-section-title {
      font-size: 14pt; font-weight: bold; color: #000;
      margin-bottom: 10px; padding-bottom: 5px; border-bottom: 2px solid #4A90E2;
    }
    .medication-item {
      margin-bottom: 10px; padding: 6px;
      border-left: 3px solid #4A90E2; background-color: #F8F9FA; border-radius: 3px;
    }
    .medication-name  { font-weight: bold; font-size: 12pt; color: #000; margin-bottom: 4px; }
    .medication-details { font-size: 10pt; color: #000; margin-bottom: 2px; line-height: 1.4; }
    .medication-warning {
      font-size: 9pt; color: #D32F2F; font-weight: bold; margin-top: 4px;
      padding: 4px 6px; background-color: #FFEBEE; border-radius: 3px; border-left: 3px solid #D32F2F;
    }
  </style>
</head>
<body>
  <div class="Section1">
    <div class="header">
      <h1>Physician's Order Sheet</h1>
      <h2>ใบสั่งการรักษาของแพทย์</h2>
    </div>

    <div class="patient-info">
      <div class="patient-info-row">
        <span class="patient-info-label">ชื่อ-สกุลผู้ป่วย:</span>
        <span class="patient-info-value">${patient.patient_name || '-'}</span>
        &nbsp;&nbsp;&nbsp;&nbsp;
        <span class="patient-info-label">HN:</span>
        <span class="patient-info-value">${patient.hn || '-'}</span>
        &nbsp;&nbsp;&nbsp;&nbsp;
        <span class="patient-info-label">อายุ:</span>
        <span class="patient-info-value">${patient.age ? `${patient.age} ปี` : '-'}</span>
      </div>
      <div class="patient-info-row">
        <span class="patient-info-label">ห้อง:</span>
        <span class="patient-info-value">${patient.room_number || '-'}</span>
        &nbsp;&nbsp;&nbsp;&nbsp;
        <span class="patient-info-label">วอร์ด:</span>
        <span class="patient-info-value">${patient.ward_name || '-'}</span>
        &nbsp;&nbsp;&nbsp;&nbsp;
        <span class="patient-info-label">วันที่รับเข้า:</span>
        <span class="patient-info-value">${registrationDate}</span>
      </div>
    </div>

    <div class="instructions">
      <strong>คำแนะนำ:</strong> กรุณาเขียนคำสั่งให้ชัดเจน ระบุวันที่และเวลาทุกครั้ง |
      One Day Order: คำสั่งที่ใช้เพียง 1 วัน |
      Continuation Order: คำสั่งที่ใช้ต่อเนื่อง
    </div>

    <table>
      <thead>
        <tr>
          <th class="col-datetime">Date / Time<br/>(วันที่ / เวลา)</th>
          <th class="col-one-day">Order For One Day<br/>(คำสั่ง 1 วัน)</th>
          <th class="col-datetime">Date / Time<br/>(วันที่ / เวลา)</th>
          <th class="col-continuation">Order For Continuation<br/>(คำสั่งใช้ต่อเนื่อง)</th>
        </tr>
      </thead>
      <tbody>
        ${generateOrderRows(patient.medications)}
      </tbody>
    </table>
  </div>
</body>
</html>`;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * แปลง scheduleTime + timing → ข้อความมื้ออาหาร
 *
 * รองรับทั้ง 2 รูปแบบ:
 *   1. ชื่อมื้อภาษาไทยโดยตรง  → ["เช้า", "กลางวัน", ...]
 *   2. time string HH:MM       → ["08:00", "12:00", ...]
 */
const TIME_TO_MEAL = {
  '07:30': 'เช้า', '08:00': 'เช้า', '08:30': 'เช้า',
  '11:30': 'กลางวัน', '12:00': 'กลางวัน', '12:30': 'กลางวัน',
  '17:30': 'เย็น',  '18:00': 'เย็น',  '18:30': 'เย็น',
  '21:00': 'ก่อนนอน',
};

function formatMealTiming(scheduleTimeRaw, timing) {
  const t = (timing || '').toLowerCase();

  if (t === 'prn' || t === 'stat' || t === 's.o.s') return 'เมื่อมีอาการ';
  if (t === 'hs') return 'ก่อนนอน';
  if (!scheduleTimeRaw) return '';

  // รองรับทั้ง Array และ string คั่นด้วย comma
  const entries = Array.isArray(scheduleTimeRaw)
    ? scheduleTimeRaw
    : scheduleTimeRaw.split(',').map(s => s.trim());

  const meals = [...new Set(
    entries.map(entry => {
      const clean = entry.replace('ก่อนอาหาร', '').replace('หลังอาหาร', '').trim();
      // รูปแบบที่ 1: ชื่อมื้อภาษาไทยโดยตรง
      if (['เช้า', 'กลางวัน', 'เย็น', 'ก่อนนอน', 'เมื่อมีอาการ'].includes(clean)) return clean;
      // รูปแบบที่ 2: HH:MM
      return TIME_TO_MEAL[clean] || clean;
    })
  )];

  const prefix = t === 'ac' ? 'ก่อนอาหาร' : t === 'pc' ? 'หลังอาหาร' : '';
  return prefix ? `${prefix} ${meals.join(' ')}` : meals.join(' ');
}

function generateOrderRows(medications) {
  const currentDate = new Date().toLocaleDateString('th-TH', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
  const currentTime = new Date().toLocaleTimeString('th-TH', {
    hour: '2-digit', minute: '2-digit',
  });

  let continuationOrders = '';

  if (medications && medications.length > 0) {
    continuationOrders =
      `<div class="medication-section-title">Medication (รายการยา)</div>` +
      medications.map((med, index) => {
        // รองรับ field names ทั้ง camelCase และ snake_case
        const medicationName =
          med.medication_name || med.medicationName ||
          med.generic_name    || med.genericName    ||
          med.trade_name      || med.tradeName      || '';

        const genericName  = med.generic_name  || med.genericName  || '';
        const tradeName    = med.trade_name    || med.tradeName    || '';
        const dosage       = med.dosage        || '';
        const route        = med.route         || '';
        const dosageInfo   = `${dosage} ${route}`.trim();
        const instruction  = med.dosage_instruction || med.dosageInstruction || med.frequency || '';

        // scheduleTime: รองรับ Array หรือ string (schedule_time_display / scheduleTime)
        const scheduleRaw  = med.scheduleTime || med.schedule_time_display || med.scheduleTimeDisplay || '';
        const timing       = formatMealTiming(scheduleRaw, med.timing);

        const specialInstruction = med.special_instruction || med.specialInstruction || '';
        const quantity     = med.quantity ? `จำนวน: ${med.quantity}` : '';
        const prnCondition = med.prn_condition || med.prnCondition || '';

        const warnings = [];
        if (med.is_external || med.isExternal) {
          const hospital = med.external_hospital || med.externalHospital || '';
          warnings.push(`⚠️ ยานอก รพ.${hospital ? ` (${hospital})` : ''}`);
        }
        if (specialInstruction) warnings.push(`⚠️ ${specialInstruction}`);
        if (prnCondition)       warnings.push(`🔔 PRN: ${prnCondition}`);

        const warningHTML = warnings.length
          ? `<div class="medication-warning">${warnings.join('<br/>')}</div>`
          : '';

        return `
          <div class="medication-item">
            <div class="medication-name">${index + 1}. ${medicationName} ${dosageInfo}</div>
            ${genericName && genericName !== medicationName
              ? `<div class="medication-details">ชื่อสามัญ: ${genericName} ${dosageInfo}</div>`
              : ''}
            ${tradeName && tradeName !== medicationName
              ? `<div class="medication-details">ชื่อทางการค้า: ${tradeName} ${dosageInfo}</div>`
              : ''}
            ${instruction ? `<div class="medication-details">วิธีใช้: ${instruction}</div>` : ''}
            ${timing      ? `<div class="medication-details">⏰ เวลา: ${timing}</div>`       : ''}
            ${quantity    ? `<div class="medication-details">${quantity}</div>`               : ''}
            ${warningHTML}
          </div>`;
      }).join('');
  }

  return `
    <tr style="height:7.9in;">
      <td class="col-datetime" style="height:7.9in;min-height:7.9in;font-size:11pt;font-weight:bold;padding-top:6px;">
        ${currentDate}<br/>${currentTime}
      </td>
      <td class="order-cell col-one-day" style="height:7.9in;min-height:7.9in;">
        <!-- พื้นที่สำหรับคำสั่ง 1 วัน -->
      </td>
      <td class="col-datetime" style="height:7.9in;min-height:7.9in;font-size:11pt;font-weight:bold;padding-top:6px;">
        ${currentDate}<br/>${currentTime}
      </td>
      <td class="order-cell col-continuation" style="height:7.9in;min-height:7.9in;">
        ${continuationOrders || '<div style="color:#999;font-style:italic;font-size:10pt;">ไม่มีคำสั่งยาต่อเนื่อง</div>'}
      </td>
    </tr>`;
}